import EmailBodyEditor from "components/EmailBodyEditor";
import Spinner from "components/Spinner";
import { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { setNotification } from "features/Notification/notificationSlice";
import authHeader from "services/auth-header";
import API from "services/axios";
import emailBodyHtml from "utils/emailBodyHtml";
import { truncateString } from "utils/UtilityFuncs";

const escapeRegExp = (value) =>
  String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const replaceVariableToken = (text, label, replacement) => {
  const normalizedLabelPattern = escapeRegExp(label).replace(/\s+/g, "\\s+");
  const pattern = new RegExp(
    `(?:&lt;&lt;|<<)\\s*${normalizedLabelPattern}\\s*(?:&gt;&gt;|>>)`,
    "gi",
  );
  return String(text || "").replace(pattern, replacement ?? "");
};

const replaceFeedbackTemplateVariables = (val, workOrder) => {
  let firstName = "";
  let lastName = "";
  if (workOrder?.contactData) {
    const contactObj =
      typeof workOrder.contactData === "string"
        ? JSON.parse(workOrder.contactData)
        : workOrder.contactData;
    firstName = contactObj?.firstName || "";
    lastName = contactObj?.lastName || "";
  }

  const replacements = {
    ID: workOrder?.woId || "",
    "Customer ID": workOrder?.customer?.customerId || "",
    "Customer Name": workOrder?.customerName || "",
    "Job Name": workOrder?.jobName || "",
    "First Name": firstName,
    "Last Name": lastName,
    "Location Name": workOrder?.location?.locationName || "",
  };

  return Object.entries(replacements).reduce(
    (updatedText, [label, replacement]) =>
      replaceVariableToken(updatedText, label, replacement),
    String(val || ""),
  );
};

const normalizeEmailHtml = (html) => {
  if (!html) return "";
  let cleaned = html.replace(/ /g, " ");
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(cleaned, "text/html");
    doc.querySelectorAll("p").forEach((p) => {
      const textOnly = (p.textContent || "").replace(/\s/g, "");
      const hasOnlyBr = p.querySelectorAll("br").length > 0 && textOnly === "";
      if (textOnly === "" && !hasOnlyBr) {
        p.remove();
      } else {
        p.style.margin = "0";
      }
    });
    doc.body.innerHTML = doc.body.innerHTML.replace(
      /(<br\s*\/?>\s*){2,}/gi,
      "<br />",
    );
    return doc.body.innerHTML;
  } catch {
    return cleaned;
  }
};

/**
 * variant "feedback" (default): Get Feedback, the location's Order Approval
 *   template's feedback fields.
 * variant "ready-pickup" (user, 2026-10-27): Ready to Pickup, the location's
 *   "Ready for Pickup" template (Settings -> Locations). Same send.
 */
const VARIANTS = {
  feedback: { title: "Get Feedback For", send: "Send Feedback" },
  "ready-pickup": { title: "Ready to Pickup", send: "Send Email" },
};

export default function SendWoFeedback({
  woId,
  selectedWO,
  refreshFunc,
  btnClass,
  btnTitle,
  variant = "feedback",
}) {
  const isPickup = variant === "ready-pickup";
  const text = VARIANTS[variant] || VARIANTS.feedback;
  const dispatch = useDispatch();

  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [userSmtp, setUserSmtp] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [subject, setSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [showHtmlEditor, setShowHtmlEditor] = useState(false);
  // Ready to Pickup edits the body in the Lexical editor, as Shipments does
  // (user, 2026-10-30: the HTML preview could not be edited and showed extra
  // spacing). Get Feedback keeps the HTML view: its template is raw HTML.
  const [bodyLoadKey, setBodyLoadKey] = useState(0);

  const checkUserLocationSmtp = useCallback(async () => {
    if (!selectedWorkOrder?.locationId) {
      setUserSmtp(null);
      return;
    }
    try {
      setIsLoading(true);
      const { data } = await API.post(
        "/user/smtp/check",
        { locationId: selectedWorkOrder?.locationId },
        { headers: authHeader() },
      );
      if (data?.success) {
        setUserSmtp({ smtpEmail: data?.smtpEmail });
      } else {
        throw new Error(data?.message || "Failed to get SMTP details");
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, selectedWorkOrder?.locationId]);

  const getWorkOrderByWoId = async (id) => {
    try {
      const { data } = await API.get("/wo-lot-approvals/with-products/" + id, {
        headers: authHeader(),
      });
      if (data?.success) {
        setSelectedWorkOrder(data?.data || null);
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to fetch work order",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.response?.data?.message || err.message,
          type: "error",
        }),
      );
    }
  };

  const reset = () => {
    if (!selectedWO) setSelectedWorkOrder(null);
    setUserSmtp(null);
    setEmailTo("");
    setSubject("");
    setEmailBody("");
    setShowHtmlEditor(false);
    setIsLoading(false);
  };

  const handleClose = () => {
    reset();
    setShowModal(false);
  };

  const sendFeedback = async () => {
    if (!userSmtp?.smtpEmail) {
      return alert("SMTP Email is required!");
    }
    if (!emailTo?.trim()) {
      return alert("Recipient email is required!");
    }
    try {
      setIsLoading(true);
      const { data } = await API.post(
        "/wo-lot-approvals/send-feedback",
        {
          wId: selectedWorkOrder?.id,
          WoId: selectedWorkOrder?.woId,
          emailTo,
          subject,
          emailBody: normalizeEmailHtml(isPickup ? emailBodyHtml(emailBody) : emailBody),
          kind: variant,
        },
        { headers: authHeader() },
      );
      if (data?.success) {
        dispatch(setNotification({ message: data.message, type: "success" }));
        if (refreshFunc) refreshFunc();
        handleClose();
      } else {
        dispatch(
          setNotification({
            message: data.message || "Failed to send feedback email",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.response?.data?.message || err.message,
          type: "error",
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedWO && !woId && showModal) {
      setSelectedWorkOrder(selectedWO);
    }
  }, [selectedWO, woId, showModal]);

  useEffect(() => {
    if (woId && !selectedWO && showModal) {
      getWorkOrderByWoId(woId);
    }
  }, [woId, selectedWO, showModal]);

  useEffect(() => {
    if (!showModal || !selectedWorkOrder) return;

    checkUserLocationSmtp();

    if (selectedWorkOrder?.contactData) {
      const contactObj =
        typeof selectedWorkOrder.contactData === "string"
          ? JSON.parse(selectedWorkOrder.contactData)
          : selectedWorkOrder.contactData;
      setEmailTo(contactObj?.email || "");
    }
  }, [showModal, selectedWorkOrder, checkUserLocationSmtp]);

  // Pull the location's "Get Feedback" template (set in Location Settings →
  // Default Module Email Templates → Get Feedback). If none is configured
  // for this location, subject/body stay blank -- no hardcoded fallback.
  useEffect(() => {
    if (!showModal || !selectedWorkOrder) return;

    const locationId =
      selectedWorkOrder?.locationId ||
      selectedWorkOrder?.location?.id ||
      selectedWorkOrder?.location?.locationId ||
      null;
    if (!locationId) return;

    const getLocationFeedbackTemplate = async () => {
      try {
        const { data } = await API.get(
          `/locations/${locationId}/templates/${isPickup ? "ready-pickup" : "approval"}`,
          { headers: authHeader() },
        );
        if (isPickup) {
          if (data?.data) {
            setSubject(
              replaceFeedbackTemplateVariables(data.data.subject, selectedWorkOrder),
            );
            setEmailBody(
              replaceFeedbackTemplateVariables(data.data.template, selectedWorkOrder),
            );
            setBodyLoadKey((k) => k + 1);
          }
          return;
        }
        if (
          (data?.success || data?.type === "success") &&
          data?.data?.sendFeedback &&
          data?.data?.feedbackTemplate
        ) {
          setSubject(
            replaceFeedbackTemplateVariables(
              data.data.feedbackSubject,
              selectedWorkOrder,
            ),
          );
          setEmailBody(
            replaceFeedbackTemplateVariables(
              data.data.feedbackTemplate,
              selectedWorkOrder,
            ),
          );
        }
      } catch (err) {
        // no template configured for this location -- leave subject/body blank
      }
    };

    getLocationFeedbackTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, selectedWorkOrder]);

  return (
    <div>
      <button
        type="button"
        className={btnClass}
        onClick={() => setShowModal(true)}
      >
        {btnTitle}
      </button>

      <Modal
        size="lg"
        contentClassName="bg-transparent"
        show={showModal}
        onHide={handleClose}
        style={{ zIndex: 1000000001 }}
      >
        <Modal.Body className="bg-white custom-shadow custom-border-radius px-4 py-3">
          {isLoading && <Spinner />}

          <h3>
            {text.title} (
            <span className="mx-2 h3">
              W-{selectedWorkOrder?.woId}{" "}
              {truncateString(selectedWorkOrder?.jobName, 30)}
            </span>
            )
          </h3>
          <hr className="mb-3 mt-1" />

          {/* Email From */}
          <div className="my-2">
            <label className="form-label">
              Email From ( User Smtp: {userSmtp?.smtpEmail} )
            </label>
            <div className="border px-2 py-1">
              <h5 className="fw-bold mb-0">
                {userSmtp?.smtpEmail
                  ? userSmtp?.smtpEmail
                  : "Please Add SMTP Settings in user settings."}
              </h5>
            </div>
          </div>

          {/* Email To */}
          <div className="mb-3">
            <label className="form-label">
              Email To{" "}
              <span className="text-muted fw-normal">
                (separate multiple by comma)
              </span>
            </label>
            <input
              type="text"
              className="form-control"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="Enter recipient email"
            />
          </div>

          {/* Subject */}
          <div className="mb-3">
            <label className="form-label">Subject</label>
            <input
              type="text"
              className="form-control"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject"
            />
          </div>

          {/* Email Body */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="form-label mb-0">Email Body</label>
              {!isPickup && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowHtmlEditor((prev) => !prev)}
              >
                {showHtmlEditor ? "Preview" : "Edit HTML"}
              </button>
              )}
            </div>

            {isPickup ? (
              <EmailBodyEditor
                html={emailBody}
                loadKey={bodyLoadKey}
                onChange={setEmailBody}
              />
            ) : showHtmlEditor ? (
              <textarea
                className="form-control font-monospace"
                rows={14}
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                style={{ fontSize: 12 }}
              />
            ) : (
              <div
                className="border rounded"
                style={{ maxHeight: 420, overflowY: "auto", padding: 8 }}
                dangerouslySetInnerHTML={{ __html: emailBody }}
              />
            )}
          </div>

          <hr className="mt-3 mb-0" />

          <div className="text-end my-3 mb-1">
            <button
              type="button"
              className="btn btn-secondary me-1"
              onClick={handleClose}
            >
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={isLoading}
              onClick={sendFeedback}
            >
              {text.send}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
